import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

class AnimationManager {
  constructor() {
    this.animations = [];
    this.scrollTriggers = [];
    this.introObserver = null;
  }

  
  initRevealAnimations() {
    
    this.killScrollTriggers();

    const reveals = document.querySelectorAll('[data-reveal]');

    reveals.forEach((el) => {
      const type = el.dataset.reveal || 'fade-up';
      const delay = parseFloat(el.dataset.revealDelay) || 0;
      const duration = parseFloat(el.dataset.revealDuration) || 0.8;
      const start = el.dataset.revealStart || 'top 85%';

      
      const initialState = this.getInitialState(type);
      gsap.set(el, initialState);

      
      const trigger = ScrollTrigger.create({
        trigger: el,
        start: start,
        once: true,
        onEnter: () => {
          gsap.to(el, {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            clipPath: 'inset(0 0% 0 0)',
            duration: duration,
            delay: delay,
            ease: 'power2.out',
          });
        },
      });

      this.scrollTriggers.push(trigger);
    });
  }

  
  getInitialState(type) {
    const states = {
      fade: { opacity: 0 },
      'fade-up': { opacity: 0, y: 40 },
      'fade-down': { opacity: 0, y: -40 },
      'fade-left': { opacity: 0, x: 40 },
      'fade-right': { opacity: 0, x: -40 },
      scale: { opacity: 0, scale: 0.9 },
      clip: { clipPath: 'inset(0 100% 0 0)' },
      'clip-up': { clipPath: 'inset(100% 0 0 0)' },
    };

    return states[type] || states.fade;
  }

  
  initStaggerAnimations() {
    const staggerContainers = document.querySelectorAll('[data-stagger]');

    staggerContainers.forEach((container) => {
      const children = container.children;
      const staggerDelay = parseFloat(container.dataset.stagger) || 0.1;
      const start = container.dataset.staggerStart || 'top 85%';

      
      gsap.set(children, { opacity: 0, y: 20 });

      const trigger = ScrollTrigger.create({
        trigger: container,
        start: start,
        once: true,
        onEnter: () => {
          gsap.to(children, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: staggerDelay,
            ease: 'power2.out',
          });
        },
      });

      this.scrollTriggers.push(trigger);
    });
  }

  
  initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');

    parallaxElements.forEach((el) => {
      const speed = parseFloat(el.dataset.parallax) || 0.5;

      const trigger = ScrollTrigger.create({
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        onUpdate: (self) => {
          const yPos = self.progress * 100 * speed - 50 * speed;
          gsap.set(el, { y: yPos });
        },
      });

      this.scrollTriggers.push(trigger);
    });
  }

  
  initImageReveals() {
    const imageContainers = document.querySelectorAll('[data-image-reveal]');

    imageContainers.forEach((container) => {
      const img = container.querySelector('img');
      if (!img) return;

      gsap.set(img, { scale: 1.2, opacity: 0 });

      const trigger = ScrollTrigger.create({
        trigger: container,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.to(img, {
            scale: 1,
            opacity: 1,
            duration: 1.2,
            ease: 'power2.out',
          });
        },
      });

      this.scrollTriggers.push(trigger);
    });
  }

  
  initIntroAnimations() {
    const introElements = document.querySelectorAll('[data-intro]:not(.intro-visible)');

    
    if (typeof window.shouldAnimate === 'function' && !window.shouldAnimate()) {
      introElements.forEach((el) => {
        el.classList.add('intro-visible');
      });
      return;
    }

    
    if (!('IntersectionObserver' in window)) {
      introElements.forEach((el) => {
        el.classList.add('intro-visible');
      });
      return;
    }

    
    if (this.introObserver) {
      this.introObserver.disconnect();
      this.introObserver = null;
    }

    
    let animationQueue = [];
    let isAnimating = false;
    const staggerDelay = 100; 

    const processQueue = () => {
      if (animationQueue.length === 0) {
        isAnimating = false;
        return;
      }

      isAnimating = true;
      const el = animationQueue.shift();

      
      el.classList.add('intro-visible');

      
      setTimeout(processQueue, staggerDelay);
    };

    
    this.introObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;

            
            this.introObserver.unobserve(el);

            
            animationQueue.push(el);

            
            if (!isAnimating) {
              processQueue();
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -10% 0px', 
        threshold: 0.1,
      },
    );

    
    introElements.forEach((el) => {
      this.introObserver.observe(el);
    });
  }

  
  splitTextReveal(element, options = {}) {
    if (!element) return null;

    const {
      timeline = null,
      duration = 1.2,
      stagger = 0.1,
      ease = 'power4.out',
      delay = 0,
      lineClass = 'split-line',
    } = options;

    
    const split = new SplitText(element, {
      type: 'lines',
      linesClass: lineClass,
    });

    
    split.lines.forEach((line, index) => {
      const wrapper = document.createElement('div');
      wrapper.style.overflow = 'hidden';
      wrapper.style.display = 'block';
      
      line.parentNode.insertBefore(wrapper, line);
      wrapper.appendChild(line);
    });

    
    gsap.set(split.lines, { yPercent: 100 });

    
    const animationConfig = {
      yPercent: 0,
      duration,
      ease,
      stagger,
    };

    let animation;
    if (timeline) {
      animation = timeline.to(split.lines, animationConfig, delay);
    } else {
      animationConfig.delay = delay;
      animation = gsap.to(split.lines, animationConfig);
    }

    return { split, animation };
  }

  
  initSplitTextAnimations() {
    const elements = document.querySelectorAll('[data-split-text]:not([data-split-initialized])');

    elements.forEach((el) => {
      const lineClass = el.dataset.splitLineClass || 'split-line';
      const duration = parseFloat(el.dataset.splitDuration) || 1.2;
      const stagger = parseFloat(el.dataset.splitStagger) || 0.1;
      const delay = parseFloat(el.dataset.splitDelay) || 0;
      const scrollTrigger = el.dataset.splitScrollTrigger !== 'false';

      
      el.dataset.splitInitialized = 'true';

      if (scrollTrigger) {
        
        const trigger = ScrollTrigger.create({
          trigger: el,
          start: 'top 85%',
          once: true,
          onEnter: () => {
            this.splitTextReveal(el, { duration, stagger, delay, lineClass });
          },
        });
        this.scrollTriggers.push(trigger);
      } else {
        
        this.splitTextReveal(el, { duration, stagger, delay, lineClass });
      }
    });
  }

  
  animateHeaderSubtitle(tl, container, startTime = 0.4) {
    const subtitleLine = container.querySelector('[data-subtitle-line]');
    const subtitleText = container.querySelector('[data-subtitle-text]');

    
    if (subtitleLine) {
      gsap.set(subtitleLine, { scaleX: 0 });
    }
    if (subtitleText) {
      gsap.set(subtitleText, { yPercent: 100 });
    }

    
    if (subtitleLine) {
      tl.to(
        subtitleLine,
        {
          scaleX: 1,
          duration: 0.8,
          ease: 'power3.out',
        },
        startTime,
      );
    }

    
    if (subtitleText) {
      tl.to(
        subtitleText,
        {
          yPercent: 0,
          duration: 0.8,
          ease: 'power4.out',
        },
        startTime + 0.2,
      );
    }
  }

  
  createHeroAnimation(container) {
    const tl = gsap.timeline();

    const title = container.querySelector('[data-hero-title]');
    const subtitle = container.querySelector('[data-hero-subtitle]');
    const cta = container.querySelector('[data-hero-cta]');
    const image = container.querySelector('[data-hero-image]');

    if (image) {
      tl.from(image, {
        scale: 1.1,
        opacity: 0,
        duration: 1.2,
        ease: 'power2.out',
      });
    }

    if (title) {
      tl.from(
        title,
        {
          y: 60,
          opacity: 0,
          duration: 0.8,
          ease: 'power2.out',
        },
        '-=0.8',
      );
    }

    if (subtitle) {
      tl.from(
        subtitle,
        {
          y: 40,
          opacity: 0,
          duration: 0.6,
          ease: 'power2.out',
        },
        '-=0.5',
      );
    }

    if (cta) {
      tl.from(
        cta,
        {
          y: 20,
          opacity: 0,
          duration: 0.5,
          ease: 'power2.out',
        },
        '-=0.3',
      );
    }

    this.animations.push(tl);
    return tl;
  }

  
  killScrollTriggers() {
    this.scrollTriggers.forEach((trigger) => trigger.kill());
    this.scrollTriggers = [];
  }

  
  killAnimations() {
    this.animations.forEach((anim) => anim.kill());
    this.animations = [];
  }

  
  refresh() {
    ScrollTrigger.refresh();
  }

  
  destroy() {
    this.killScrollTriggers();
    this.killAnimations();
    if (this.introObserver) {
      this.introObserver.disconnect();
      this.introObserver = null;
    }
  }
}

export const animationManager = new AnimationManager();
export default animationManager;
